import React, { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Lead } from '@/types/lead';
import { format } from 'date-fns';
import { Badge } from '@/components/ui/badge';
import { Mail, CheckCircle2, XCircle, Eye, MousePointerClick, Clock, Send } from 'lucide-react';

interface OutreachEmail {
  id: string;
  subject: string;
  recipientEmail: string;
  status: string;
  deliveryStatus: string | null;
  sentAt: string | null;
  campaignId: string | null;
  campaignName: string | null;
}

interface OutreachTabProps {
  lead: Lead;
}

const OutreachTab: React.FC<OutreachTabProps> = ({ lead }) => {
  const [emails, setEmails] = useState<OutreachEmail[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchOutreach = async () => {
      setLoading(true);
      try {
        // Fetch sent emails from email_queue for this lead
        const { data: queueItems, error } = await supabase
          .from('email_queue')
          .select('id, subject, recipient_email, status, delivery_status, sent_at, campaign_id')
          .eq('lead_id', lead.id)
          .eq('status', 'sent')
          .order('sent_at', { ascending: false });

        if (error) throw error;

        // Get unique campaign IDs to fetch names
        const campaignIds = [...new Set((queueItems || []).map(q => q.campaign_id).filter(Boolean))];
        let campaignMap: Record<string, string> = {};
        
        if (campaignIds.length > 0) {
          const { data: campaigns } = await supabase
            .from('email_campaigns')
            .select('id, name')
            .in('id', campaignIds);
          
          if (campaigns) {
            campaignMap = Object.fromEntries(campaigns.map(c => [c.id, c.name]));
          }
        }

        setEmails((queueItems || []).map(q => ({
          id: q.id,
          subject: q.subject,
          recipientEmail: q.recipient_email,
          status: q.status,
          deliveryStatus: q.delivery_status,
          sentAt: q.sent_at,
          campaignId: q.campaign_id,
          campaignName: q.campaign_id ? campaignMap[q.campaign_id] || 'Unknown Campaign' : null,
        })));
      } catch (err) {
        console.error('Error fetching outreach data:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchOutreach();
  }, [lead.id]);

  const getStatusIcon = (deliveryStatus: string | null) => {
    switch (deliveryStatus) {
      case 'delivered': return <CheckCircle2 className="h-4 w-4 text-green-500" />;
      case 'bounced': return <XCircle className="h-4 w-4 text-destructive" />;
      case 'opened': return <Eye className="h-4 w-4 text-blue-500" />;
      case 'clicked': return <MousePointerClick className="h-4 w-4 text-purple-500" />;
      case 'dropped': return <XCircle className="h-4 w-4 text-orange-500" />;
      default: return <Send className="h-4 w-4 text-muted-foreground" />;
    }
  };

  const getStatusBadge = (deliveryStatus: string | null) => {
    switch (deliveryStatus) {
      case 'delivered': return <Badge variant="outline" className="text-green-600 border-green-300 bg-green-50">Delivered</Badge>;
      case 'bounced': return <Badge variant="destructive">Bounced</Badge>;
      case 'opened': return <Badge variant="outline" className="text-blue-600 border-blue-300 bg-blue-50">Opened</Badge>;
      case 'clicked': return <Badge variant="outline" className="text-purple-600 border-purple-300 bg-purple-50">Clicked</Badge>;
      case 'dropped': return <Badge variant="outline" className="text-orange-600 border-orange-300 bg-orange-50">Dropped</Badge>;
      default: return <Badge variant="secondary">Sent</Badge>;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Clock className="h-5 w-5 animate-spin text-muted-foreground mr-2" />
        <span className="text-muted-foreground">Loading outreach history...</span>
      </div>
    );
  }

  if (emails.length === 0) {
    return (
      <div className="text-center py-12">
        <Mail className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
        <h3 className="text-lg font-semibold mb-1">No emails sent</h3>
        <p className="text-sm text-muted-foreground">No campaign emails have been sent to this lead yet.</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
          Outreach History
        </h3>
        <Badge variant="secondary" className="text-xs">{emails.length} email{emails.length !== 1 ? 's' : ''}</Badge>
      </div>

      <div className="space-y-2">
        {emails.map((email) => (
          <div key={email.id} className="flex items-start gap-3 p-3 rounded-lg border bg-card hover:bg-accent/50 transition-colors">
            <div className="mt-0.5">{getStatusIcon(email.deliveryStatus)}</div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-medium text-sm truncate">{email.subject}</span>
                {getStatusBadge(email.deliveryStatus)}
              </div>
              <div className="flex items-center gap-2 mt-1 text-xs text-muted-foreground">
                {email.campaignName && (
                  <span className="truncate">Campaign: {email.campaignName}</span>
                )}
                {email.sentAt && (
                  <>
                    <span>•</span>
                    <span>{format(new Date(email.sentAt), 'MMM d, yyyy h:mm a')}</span>
                  </>
                )}
              </div>
              <div className="text-xs text-muted-foreground mt-0.5">
                To: {email.recipientEmail}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default OutreachTab;
